import { useRef, useState } from "react";
import { Download, RotateCcw, Upload } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { DelegationsValidationReport } from "@/features/delegations/components/DelegationsValidationReport";
import {
  useActivateDelegationVersion,
  useCanManageDelegations,
  useDelegationsOverview,
} from "@/features/delegations/hooks/useDelegationsQueries";
import { delegationsService } from "@/features/delegations/services/delegationsService";
import type { DelegationValidationReport } from "@/types/delegations";

/**
 * الإيفادات الرسمية — replace the data behind the public record table.
 *
 * The home page's «الايفادات الرسمية» tile used to open a Power BI "publish to web"
 * embed; the site renders the table itself now, and this page is the only way its
 * records are updated. Gated on `delegations:manage` (SUPER_ADMIN-only by baseline):
 * an upload republishes 4,390 named officials, their destinations and who paid.
 *
 * 🔑 THE TWO-STEP IS THE WHOLE DESIGN, and it is the OBS one for the same reason.
 * «فحص» is a pure dry run against the live data; «نشر» re-runs the identical checks
 * server-side and refuses on any error. A filtered export that would silently drop
 * three quarters of the records cannot reach the public site without a person being
 * shown exactly that and confirming it.
 */

/**
 * 🔴 WESTERN DIGITS, AND THAT IS THE MINISTRY'S OWN CONVENTION rather than a shortcut.
 * `mof-frontend/src/lib/obs/types.ts` states it — "years render in Arabic-Indic digits
 * (٢٠٢٤) and measures in Western digits" — and the OBS validation report next door
 * formats `en-US` for the same reason.
 *
 * ⚠️ THIS WAS `ar-EG` AND IT SHIPPED A VISIBLE DEFECT, reported on the public screen
 * first and here second: `ar-EG` groups with U+066C ARABIC THOUSANDS SEPARATOR, which
 * the Arabic UI face draws at CAP HEIGHT — «٤٬٣٩٠» reads as a stray apostrophe floating
 * above the digits instead of a grouping mark.
 * 🔑 The trap is the GROUPING, not the locale: it appears at 1,000 and above and never
 * below, which is why a version number and a page count never showed it.
 */
const fmt = new Intl.NumberFormat("en-US");
/**
 * `ar-IQ`, matching ObsScreenCard's own `formatDate` — this is the Iraqi ministry, and the
 * version-history row next door is the thing this one mirrors.
 * ⚠️ VISUALLY IDENTICAL to the `ar-EG` it replaces (both render ١١‏/٠٩‏/٢٠٢٦ here); the
 * change is which locale we mean, not what it draws.
 *
 * 🔑 AND THE DATE STAYS ARABIC-INDIC WHILE THE COUNT BESIDE IT IS WESTERN. That looks like
 * a mix, and it is the established one: ObsScreenCard pairs `Intl.NumberFormat("en-US")`
 * with an `ar-IQ` date in this exact row. It also follows the ministry's stated convention
 * — measures in Western digits, dates and years in Arabic-Indic.
 */
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("ar-IQ", { dateStyle: "medium" }) : "—";

export default function DelegationsPage() {
  const canManage = useCanManageDelegations();
  const { data, isLoading, error, refetch } = useDelegationsOverview(canManage);
  const activate = useActivateDelegationVersion();

  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<DelegationValidationReport | null>(null);
  const [busy, setBusy] = useState<"validate" | "publish" | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const slug = data?.slug ?? "official-delegations";
  const active = data?.versions.find((v) => v.isActive) ?? null;

  /**
   * ⚠️ A 422 OR A 409 IS A NORMAL RETURN VALUE HERE, not a throw — the routes sit
   * outside the backend's response envelope precisely so the report survives a
   * non-2xx status. `catch` is therefore for transport failures only; treating a
   * rejection as an exception would discard the reason it was rejected.
   */
  async function run(kind: "validate" | "publish", force = false) {
    if (!file) return;
    setBusy(kind);
    setFailure(null);
    try {
      const result =
        kind === "validate"
          ? await delegationsService.validate(slug, file)
          : await delegationsService.upload(slug, file, force);
      setReport(result);
      if (kind === "publish" && result.applied) {
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
        await refetch();
      }
    } catch (e) {
      setFailure((e as Error)?.message ?? "تعذّر إتمام الطلب.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <AdminPageShell
      title="الإيفادات الرسمية"
      description="تحديث جدول الإيفادات المنشور على الموقع عبر رفع ملف Excel."
      isLoading={isLoading}
      error={error as Error | null}
    >
      {!canManage ? (
        <div className="rounded-lg border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
          لا تملك صلاحية إدارة الإيفادات الرسمية.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            كل رفع يُنشئ إصدارًا جديدًا ولا يحذف السابق، ويُفحص الملف قبل نشره. للتراجع
            عن نشر، فعّل الإصدار السابق من السجل أدناه.
          </div>

          <section className="rounded-xl border border-border p-4">
            <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-base font-semibold">الجدول المنشور حاليًا</h2>
                <p className="text-sm text-muted-foreground">
                  {active
                    ? `الإصدار ${fmt.format(active.version)} · ${fmt.format(active.stats?.rowCount ?? 0)} سجل · ${fmtDate(active.activatedAt)}`
                    : "لا يوجد إصدار منشور بعد — يعرض الموقع النسخة المرفقة بالمشروع."}
                </p>
              </div>
              {active?.fileUrl ? (
                <Button variant="outline" size="sm" asChild>
                  <a href={active.fileUrl} download>
                    <Download className="size-4" />
                    تنزيل الملف المصدر
                  </a>
                </Button>
              ) : null}
            </header>

            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                className="block max-w-full text-sm file:me-3 file:rounded-md file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-sm"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  // A new file invalidates the previous verdict; leaving it on
                  // screen would invite publishing file B on file A's report.
                  setReport(null);
                  setFailure(null);
                }}
              />
              <Button
                variant="outline"
                disabled={!file || busy !== null}
                onClick={() => run("validate")}
              >
                {busy === "validate" ? <Spinner className="size-4" /> : null}
                فحص
              </Button>
              <Button
                disabled={!file || busy !== null || (report?.errors.length ?? 0) > 0}
                onClick={() => run("publish", (report?.warnings.length ?? 0) > 0)}
              >
                {busy === "publish" ? (
                  <Spinner className="size-4" />
                ) : (
                  <Upload className="size-4" />
                )}
                {(report?.warnings.length ?? 0) > 0 ? "نشر رغم التنبيهات" : "نشر"}
              </Button>
            </div>

            {failure ? (
              <p className="mt-3 text-sm text-destructive">{failure}</p>
            ) : null}
            {report ? (
              <div className="mt-4">
                <DelegationsValidationReport
                  report={report}
                  previousStats={active?.stats ?? null}
                />
              </div>
            ) : null}
          </section>

          <section className="rounded-xl border border-border p-4">
            <h2 className="mb-3 text-base font-semibold">سجل الإصدارات</h2>
            {!data?.versions.length ? (
              <p className="text-sm text-muted-foreground">لا توجد إصدارات بعد.</p>
            ) : (
              <ul className="divide-y divide-border">
                {data.versions.map((v) => (
                  <li
                    key={v.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-3"
                  >
                    {/*
                      `space-y-1` is the fix for a reported defect, not decoration: the
                      version line and the detail line under it sat flush against each
                      other, so «الإصدار ٢» and its row count read as one wrapped
                      sentence rather than a heading and its metadata.
                    */}
                    <div className="min-w-0 space-y-1">
                      <div className="text-sm font-medium">
                        الإصدار <bdi>{fmt.format(v.version)}</bdi>
                        {v.isActive ? (
                          <span className="ms-2 rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-700">
                            منشور
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <bdi>{fmt.format(v.stats?.rowCount ?? 0)}</bdi> سجل ·{" "}
                        {fmtDate(v.createdAt)}
                        {v.sourceFilename ? ` · ${v.sourceFilename}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {v.fileUrl ? (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={v.fileUrl} download>
                            <Download className="size-4" />
                          </a>
                        </Button>
                      ) : null}
                      {/*
                        🔑 THE RESTORE BUTTON APPEARS ON EVERY VERSION THAT IS NOT THE
                        LIVE ONE, and it says «استعادة هذا الإصدار» rather than «تفعيل».
                        It was reported missing, and the likely reason is that with a
                        single uploaded version there is nothing to restore TO — the one
                        row is the live one — so the column looked empty. The wording is
                        the part worth fixing: "activate" describes the mechanism, and an
                        operator looking to undo a bad publication is scanning for the
                        word restore.
                        The active row gets an explicit «الإصدار الحالي» instead of a
                        blank, so the absence of a button reads as a state rather than as
                        something failing to render.
                      */}
                      {v.isActive ? (
                        <span className="text-xs text-muted-foreground">
                          الإصدار الحالي
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={activate.isPending}
                          onClick={() => activate.mutate({ slug, version: v.version })}
                        >
                          <RotateCcw className="size-4" />
                          استعادة هذا الإصدار
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </AdminPageShell>
  );
}
